import { useMemo, useState, useEffect } from "react";
import { endOfDay, endOfMonth, format, startOfDay } from "date-fns";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Legend,
    Tooltip,
    ReferenceArea,
    ReferenceLine,
} from "recharts";
import type { DateRange } from "react-day-picker";
/* import { Button } from "../../ui/button"; */
import { Card, CardContent, CardHeader } from "../../ui/card";
import { DateRangePicker } from "../../ui/date-range-picker";
import { /* FilterDropdown, */ type FilterOption } from "../../ui/filter-dropdown";
import { ExportMenu } from "../../ui/export-menu";
/* import { X } from "lucide-react"; */
import { SummaryHeader } from "../../ui/SummaryHeader";
import { useChartColors } from "../../../hooks/useChartColors";
import DataLoader from "../../../components/loaders/DataLoader";
import { MonthRangePicker } from "../../ui/month-year-range";
import { useGetEcomEventMonth } from "../../calendar/hooks/useGetEcomEvent";
import type { EventByDate } from "../../calendar/services/getEventsMonthService";
import ChartCard from "../../../components/chartEventCard/chartcard";
import type { CalendarEvent } from "../../../types/types";
import { useEcommRevenueBreakdown } from "../hooks/useEcommRevenueBreakdown";
import { formatCurrency } from "../../../utils/formatCurrency";



export const comparisonOptions: FilterOption[] = [
    { label: "Revenue & Profit", value: "profit" },
    { label: "Revenue Only", value: "no_comparison" },
];

export const granularityOptions: FilterOption[] = [
    { label: "Daily", value: "daily" },
    { label: "Monthly", value: "monthly" },
];

export function EcomRevenueChart() {
    const now = new Date();
    const [dateRange, setDateRange] = useState<DateRange | undefined>({
        from: new Date(now.getFullYear(), now.getMonth(), 1),
        to: now,
    });
    const [comparison, _setComparison] = useState<string>("profit");
    const [granularity, _setGranularity] = useState<string>("daily");
    const [_channel, _setChannel] = useState<string>("shopify");
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

    const selectedMonth = dateRange?.from
        ? `${dateRange.from.getFullYear()}-${String(dateRange.from.getMonth() + 1).padStart(2, "0")}`
        : "";

    const { data: eventsData, isLoading: eventsLoading } = useGetEcomEventMonth(selectedMonth);
    const events = eventsData ? mapToCalendarEvents(eventsData) : [];

    const windowDays = 3;

    const params = useMemo(() => ({
        start_date: dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
        end_date: dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
        channel: "shopify",
        kind: comparison,
        period_type: granularity,
    }), [dateRange, comparison, granularity]);

    const colors = useChartColors("ecommerce") || ["#8884d8", "#82ca9d", "#ffc658", "#ff7300"];
    const { data, isLoading } = useEcommRevenueBreakdown(params);

    // Use state to persist data and trigger re-renders
    const [processedData, setProcessedData] = useState<any[]>([]);

    useEffect(() => {
        if (data) {
            setProcessedData(applyGranularity(data, granularity));
        }
    }, [data, granularity]);

    /* const resetFilters = () => {
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setDateRange({ from: firstDayOfMonth, to: lastDayOfMonth });
      setComparison("profit");
      setGranularity("daily");
      setChannel("shopify");
    };
   */
    const allYValues = processedData.flatMap((d) => [
        d.revenue_value,
        comparison === "profit" ? d.profit_value : null,
        comparison === "last_period" ? d.previous_revenue : null,
        comparison === "last_year" ? d.last_year : null,
    ]).filter((v): v is number => typeof v === "number");

    const minY = Math.min(...allYValues);
    const maxY = Math.max(...allYValues);
    const yMin = minY > 0 ? 0 : minY * 1.1;
    const yMax = maxY * 1.1;

    const smartFormatter = (value: number) => {
        if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
        if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(0)}k`;
        return `$${value}`;
    };

    // Handle empty states
    const isDataEmpty = processedData.length === 0;
    const isEventsEmpty = events.length === 0;

    // Filter processedData for chart display
    const chartStart = dateRange?.from ? startOfDay(dateRange.from).getTime() : null;
    const chartEnd = dateRange?.to ? endOfDay(dateRange.to).getTime() : null;

    const filteredChartData = processedData.filter(d => {
        const t = new Date(d.date).getTime();
        return (!chartStart || t >= chartStart) && (!chartEnd || t <= chartEnd);
    });

    return (
        <>
            <SummaryHeader title="Revenue Breakdown" />
            <Card className="flex flex-col h-full relative">
                <CardHeader>
                    {/* Filters and Controls */}
                    <div className="flex flex-wrap gap-2 w-full items-start">
                        <div className="min-w-[180px] max-w-full">
                            {granularity === "monthly" ? (
                                <MonthRangePicker
                                    value={{ start: dateRange?.from, end: dateRange?.to }}
                                    onChange={({ start, end }) => {
                                        setDateRange({ from: start, to: end });
                                    }}
                                    className="w-full"
                                />
                            ) : (
                                <DateRangePicker
                                    dateRange={dateRange}
                                    onDateRangeChange={setDateRange}
                                    className="w-full"
                                />
                            )}
                        </div>

                        {/* <div className="min-w-[150px] max-w-full">
              <FilterDropdown
                options={comparisonOptions}
                value={comparison}
                onChange={setComparison}
                placeholder="Comparison"
                triggerClassName="w-full"
              />
            </div> */}
                        {/* 
            <div className="min-w-[150px] max-w-full">
              <FilterDropdown
                options={granularityOptions}
                value={granularity}
                onChange={setGranularity}
                placeholder="Granularity"
                triggerClassName="w-full"
              />
            </div> */}

                        {/* <div className="min-w-[100px] max-w-full">
              <Button size="sm" onClick={resetFilters} className="w-full">
                <X className="h-4 w-4 mr-1" />
                Reset
              </Button>
            </div> */}

                        <div className="min-w-[100px] max-w-full">
                            <ExportMenu
                                data={processedData}
                                filename="revenue_data"
                                buttonSize="sm"
                            />
                        </div>
                    </div>
                </CardHeader>
                {isLoading || eventsLoading ? (
                    <DataLoader variant="revenue-chart" itemCount={1} />
                ) : (
                    <CardContent className="flex gap-4 h-full">
                        {/* Chart Section */}
                        <div className="flex-1">
                            {isDataEmpty ? (
                                <div className="flex items-center justify-center h-full">
                                    <p>No revenue data available.</p>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={400}>
                                    <AreaChart
                                        data={filteredChartData}
                                        margin={{ top: 10, right: 10, left: 4, bottom: 0 }}
                                    >
                                        <defs>
                                            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.5} />
                                                <stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid
                                            stroke="var(--chart-grid)"
                                            vertical={false}
                                            strokeDasharray="4 4"
                                            strokeWidth="1"
                                        />
                                        <XAxis
                                            dataKey="date"
                                            className="text-xs sm:text-sm text-muted-foreground"
                                            tickLine={false}
                                            axisLine={true}
                                            tick={{ fontSize: 12 }}
                                            tickMargin={8}
                                            interval="preserveStartEnd"
                                            minTickGap={30}
                                            tickFormatter={(date) =>
                                                granularity === "monthly"
                                                    ? format(new Date(date), "MMM yyyy")
                                                    : format(new Date(date), "MMM d")
                                            }
                                        />
                                        <YAxis
                                            className="text-xs sm:text-sm text-muted-foreground"
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={smartFormatter}
                                            width={60}
                                            domain={[yMin, yMax]}
                                        />
                                        <Tooltip
                                            labelFormatter={(label) =>
                                                granularity === "monthly"
                                                    ? format(new Date(label), "MMM yyyy")
                                                    : format(new Date(label), "MMM d yyyy")
                                            }
                                            formatter={(value) => formatCurrency(Number(value))}
                                            contentStyle={{
                                                backgroundColor: "var(--background)",
                                                borderColor: "var(--border)",
                                                borderRadius: "var(--radius)",
                                                fontSize: "0.875rem",
                                            }}
                                            itemStyle={{ color: "var(--chart-text)" }}
                                        />
                                        {events.map((evt) => {
                                            const isSingleDay = evt.startAt === evt.endAt;
                                            const isSelected = selectedEvent?.id === evt.id;
                                            
                                            if (isSingleDay) {
                                                return (
                                                    <ReferenceLine
                                                        key={evt.id}
                                                        x={evt.startAt}
                                                        stroke={isSelected ? "var(--chart-1)" : "var(--accent)"}
                                                        strokeWidth={isSelected ? 3 : 2}
                                                        strokeDasharray="5 5"
                                                        label={{
                                                            value: evt.title.length > 15 ? evt.title.substring(0, 15) + '...' : evt.title,
                                                            position: "top",
                                                            fill: "var(--muted-foreground)",
                                                            fontSize: 10,
                                                        }}
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => setSelectedEvent(evt)}
                                                    />
                                                );
                                            } else {
                                                return (
                                                    <ReferenceArea
                                                        key={evt.id}
                                                        x1={evt.startAt}
                                                        x2={evt.endAt}
                                                        stroke={isSelected ? "var(--chart-1)" : "var(--accent)"}
                                                        strokeWidth={2}
                                                        fill="var(--accent)"
                                                        fillOpacity={isSelected ? 0.2 : 0.1}
                                                        label={{
                                                            value: evt.title.length > 15 ? evt.title.substring(0, 15) + '...' : evt.title,
                                                            position: "top",
                                                            fill: "var(--muted-foreground)",
                                                            fontSize: 10,
                                                        }}
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => setSelectedEvent(evt)}
                                                    />
                                                );
                                            }
                                        })}
                                        <Area
                                            type="monotone"
                                            dataKey="revenue_value"
                                            name="Revenue"
                                            stroke={colors[3]}
                                            fill={colors[3]}
                                            fillOpacity={0.2}
                                            activeDot={{ r: 6 }}
                                            strokeWidth={2}
                                        />
                                        {comparison === "profit" && (
                                            <Area
                                                type="monotone"
                                                dataKey="profit_value"
                                                name="Profit"
                                                stroke={colors[2]}
                                                fill={colors[2]}
                                                fillOpacity={0.2}
                                                strokeWidth={2}
                                            />
                                        )}
                                        <Legend
                                            verticalAlign="top"
                                            align="right"
                                            wrapperStyle={{
                                                paddingBottom: 20,
                                                marginRight: -10,
                                                marginTop: -24,
                                            }}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        {/* Events Sidebar */}
                        {isEventsEmpty ? (
                            <div className="w-80 h-full flex items-center justify-center bg-muted/20 rounded-lg border border-border/50">
                                <p>No events available.</p>
                            </div>
                        ) : (
                            <ChartCard
                                events={events}
                                revenueData={data ?? []}
                                currency="$"
                                granularity={granularity}
                                windowDays={windowDays}
                                onEventSelect={setSelectedEvent}
                                selectedEvent={selectedEvent}
                                isLoading={isLoading}
                            />
                        )}
                    </CardContent>


                )}
            </Card>
        </>
    );
}

function applyGranularity(data: any[], granularity: string) {
    if (granularity === "daily") return data;

    if (granularity === "monthly") {
        const monthlyData: any[] = [];
        const months: Record<string, any> = {};

        data.forEach((item) => {
            const date = new Date(item.date);
            const monthKey = format(date, "MMM yyyy");

            if (!months[monthKey]) {
                months[monthKey] = {
                    date: endOfMonth(date).toISOString(),
                    revenue_value: 0,
                    profit_value: 0,
                    previous_revenue: 0,
                    last_year: 0,
                    count: 0,
                };
            }

            months[monthKey].revenue_value += item.revenue_value || 0;
            months[monthKey].profit_value += item.profit_value || 0;
            months[monthKey].previous_revenue += item.previous_revenue || 0;
            months[monthKey].last_year += item.last_year || 0;
            months[monthKey].count += 1;
        });

        Object.values(months).forEach((month) => {
            monthlyData.push({
                date: month.date,
                revenue_value: Math.round(month.revenue_value),
                profit_value: Math.round(month.profit_value),
                previous_revenue: Math.round(month.previous_revenue),
                last_year: Math.round(month.last_year),
            });
        });

        return monthlyData;
    }
    return data;
}

function mapToCalendarEvents(events: EventByDate[]): CalendarEvent[] {
    return events.map((event) => ({
        id: event.id.toString(),
        title: event.title,
        description: event.description,
        source: event.source as "manual" | "meta" | "clayrio",
        channel: event.channel as "ecommerce" | "retail" | "wholesale",
        location: event.location || "",
        startAt: event.start_at,
        endAt: event.end_at,
    }));
}